import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Checkbox,
  FormControlLabel,
  makeStyles,
  Tabs,
  Tab,
} from "@material-ui/core";
import { Alert, Autocomplete } from "@material-ui/lab";
import { PersonAdd } from "@material-ui/icons";
import { useAuth } from "../context/ApiKeyContext";
import ServiceTree from "./ServiceTree";
import { useTranslation } from "../hooks/useTranslation";
import { useUserManagement } from "../utils/userManagement";
import { getApiBaseUrl } from "../config";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    backgroundColor: "var(--ikaro-orange-primary)",
    color: "white",
    padding: theme.spacing(2),
  },
  dialogContent: {
    padding: theme.spacing(3),
    maxHeight: "80vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  scrollableBody: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
  },
  formField: {
    marginBottom: theme.spacing(2),
    "& .MuiTextField-root": {
      height: "56px",
    },
    "& .MuiFormControl-root": {
      height: "56px",
    },
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      height: "56px",
    },
  },
  selectField: {
    "& .MuiOutlinedInput-root": {
      height: "56px",
    },
  },
  selectedProfiles: {
    marginTop: theme.spacing(1),
    maxHeight: "50px",
    overflowY: "auto",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
  },
  profileChip: {
    margin: 0,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100px",
  },
  selectMenu: {
    maxHeight: "180px",
    "& .MuiPaper-root": {
      maxHeight: "180px !important",
    },
  },
  menuItem: {
    padding: theme.spacing(1, 2),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  tabPanel: {
    padding: theme.spacing(2, 0),
  },
  rulesTreeContainer: {
    height: "min(55vh, 420px)",
    minHeight: 280,
    overflow: "auto",
    border: "1px solid var(--ikaro-gray-medium)",
    borderRadius: "8px",
    padding: theme.spacing(1),
  },
  selectedRulesInfo: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: "var(--ikaro-gray-light)",
    borderRadius: "4px",
  },
}));

const CreateUserModal = ({
  open,
  onClose,
  onUserCreated,
  fetchServices,
  token,
}) => {
  const classes = useStyles();
  const { hasRole, user } = useAuth();
  const { t } = useTranslation("jobs");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    profiles: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRules, setSelectedRules] = useState([]);
  const [selectedProductRules, setSelectedProductRules] = useState([]);
  const [rulesValidationError, setRulesValidationError] = useState(null);
  const API_BASE_URL = getApiBaseUrl();
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [cells, setCells] = useState([]);
  const [selectedCells, setSelectedCells] = useState([]);
  const [loadingCells, setLoadingCells] = useState(false);

  // Hook para gerenciar companies e tenants
  const {
    companyOptions,
    loadingCompanies,
    selectedCompany,
    searchCompanies,
    selectCompany,
    tenantOptions,
    loadingTenants,
    selectedTenant,
    setSelectedTenant,
    searchTenants,
  } = useUserManagement();

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Função para lidar com mudança de aba
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setRulesValidationError(null);
  };

  // userServices/userProducts para o ServiceTree refletir as regras selecionadas (mesmo formato do modal de detalhes)
  const userServicesForTree = React.useMemo(() => {
    const byService = {};
    selectedRules.forEach(({ serviceId, ruleId }) => {
      if (!byService[serviceId]) byService[serviceId] = { service_id: serviceId, rules: [] };
      byService[serviceId].rules.push({ rule_id: ruleId });
    });
    return Object.values(byService);
  }, [selectedRules]);

  const userProductsForTree = React.useMemo(() => {
    const byProduct = {};
    selectedProductRules.forEach(({ productCode, ruleId }) => {
      if (!byProduct[productCode]) byProduct[productCode] = { product_code: productCode, rules: [] };
      byProduct[productCode].rules.push({ rule_id: ruleId });
    });
    return Object.values(byProduct);
  }, [selectedProductRules]);

  // ServiceTree chama onRuleToggle(userId, service_rule_id, checked). rule_id pode ser number ou UUID — não converter para Number.
  const handleRuleToggle = (userId, service_rule_id, checked) => {
    const service = services.find((s) => s.rules?.some((r) => String(r.rule_id) === String(service_rule_id)));
    if (!service) return;
    const sid = service.service_id;
    const rid = service_rule_id;
    setSelectedRules((prev) => {
      if (checked) {
        const exists = prev.some((r) => String(r.serviceId) === String(sid) && String(r.ruleId) === String(rid));
        return exists ? prev : [...prev, { serviceId: sid, ruleId: rid }];
      }
      return prev.filter((r) => !(String(r.serviceId) === String(sid) && String(r.ruleId) === String(rid)));
    });
    setRulesValidationError(null);
  };

  const handleProductRuleToggle = (userId, product_code, rule_id, checked) => {
    setSelectedProductRules((prev) => {
      if (checked) {
        const exists = prev.some((r) => r.productCode === product_code && String(r.ruleId) === String(rule_id));
        return exists ? prev : [...prev, { productCode: product_code, ruleId: rule_id }];
      }
      return prev.filter((r) => !(r.productCode === product_code && String(r.ruleId) === String(rule_id)));
    });
    setRulesValidationError(null);
  };

  // Mesma ideia de UserManagement → associate_service: ligar/desligar todas as regras globais do serviço
  const handleServiceToggle = async (userId, serviceId, checked) => {
    const service = services.find((s) => String(s.service_id) === String(serviceId));
    if (!service?.rules?.length) return;
    setSelectedRules((prev) => {
      const rest = prev.filter((r) => String(r.serviceId) !== String(service.service_id));
      if (!checked) return rest;
      const merged = [...rest];
      for (const rule of service.rules) {
        const rid = rule.rule_id;
        const dup = merged.some(
          (r) =>
            String(r.serviceId) === String(service.service_id) &&
            String(r.ruleId) === String(rid),
        );
        if (!dup) merged.push({ serviceId: service.service_id, ruleId: rid });
      }
      return merged;
    });
    setRulesValidationError(null);
  };

  // Validar se pelo menos uma regra (serviço ou produto) foi selecionada
  const validateRules = () => {
    if (selectedRules.length === 0 && selectedProductRules.length === 0) {
      setRulesValidationError("Selecione pelo menos uma regra para continuar");
      return false;
    }
    setRulesValidationError(null);
    return true;
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setFormData({
        name: "",
        email: "",
        profiles: [],
      });
      setError(null);
      setSuccess(false);
      setActiveTab(0);
      setSelectedRules([]);
      setSelectedProductRules([]);
      setSelectedCells([]);
      setCells([]);
      setRulesValidationError(null);

      // Carregar lista completa de companies quando modal abrir
      if (hasRole("ADMIN", "admin")) {
        searchCompanies();
      }
    }
  }, [open, hasRole, searchCompanies]);

  // Buscar serviços quando o tenant mudar ou quando o modal abrir
  useEffect(() => {
    if (open) {
      // Se for admin e tiver tenant selecionado, usar esse tenant
      // Se não for admin, usar o tenant do usuário logado
      const tenantToUse = hasRole("ADMIN", "admin")
        ? selectedTenant?.id || user?.tenant_id
        : user?.tenant_id;

      if (tenantToUse) {
        fetchServices(tenantToUse)
          .then((data) => {
            setServices(data?.services || []);
            setProducts(data?.products || []);
          })
          .catch((err) => {
            console.error("Erro ao buscar serviços:", err);
            setServices([]);
            setProducts([]);
          });
      } else {
        setServices([]);
        setProducts([]);
      }
    }
  }, [open, hasRole, user?.tenant_id, selectedTenant?.id, fetchServices]);

  // Buscar células do tenant quando abrir a aba Células ou quando tiver tenant
  const tenantIdForCells = hasRole("ADMIN", "admin") ? selectedTenant?.id : (selectedTenant?.id ?? user?.tenant_id);
  useEffect(() => {
    if (!open || !tenantIdForCells) return;
    setLoadingCells(true);
    fetch(`${API_BASE_URL}/cells/by_tenant/${tenantIdForCells}`, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("Erro ao buscar células"))))
      .then((data) => setCells(data.data || []))
      .catch(() => setCells([]))
      .finally(() => setLoadingCells(false));
  }, [open, tenantIdForCells, token, API_BASE_URL]);

  const handleCellToggle = (cellId, checked) => {
    setSelectedCells((prev) =>
      checked ? [...prev, cellId] : prev.filter((id) => id !== cellId)
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    if (!validateRules()) {
      setLoading(false);
      return;
    }

    try {
      const bodyData = {
        name: formData.name,
        email: formData.email,
        service_rules: selectedRules.map((rule) => ({
          service_id: rule.serviceId,
          service_rule_id: rule.ruleId,
        })),
      };

      const tenantId = hasRole("ADMIN", "admin") && selectedTenant
        ? selectedTenant.id
        : user?.tenant_id ?? selectedTenant?.id;
      if (tenantId != null) {
        bodyData.tenant_id = tenantId;
      }

      const response = await fetch(`${API_BASE_URL}/users/create_user`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            errorData.detail ||
            `Erro ao criar usuário: ${response.status}`
        );
      }

      const result = await response.json();
      const newUserId = result.user_id;

      // Associar regras de produto após criar o usuário
      if (newUserId && selectedProductRules.length > 0) {
        for (const { productCode, ruleId } of selectedProductRules) {
          const res = await fetch(`${API_BASE_URL}/users/associate_product_rule`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              user_id: newUserId,
              product_code: productCode,
              rule_id: ruleId,
            }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            console.warn("Erro ao associar regra de produto:", productCode, ruleId, err);
          }
        }
      }

      // Associar células selecionadas
      if (newUserId && selectedCells.length > 0) {
        for (const cellId of selectedCells) {
          const res = await fetch(`${API_BASE_URL}/user_cells/associate`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ user_id: newUserId, cell_id: cellId }),
          });
          if (!res.ok) console.warn("Erro ao associar célula:", cellId);
        }
      }

      setSuccess(true);
      setTimeout(() => {
        onUserCreated();
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center">
          <PersonAdd className="mr-8" />
          {t("header.newUser")}
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {loading && (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        )}

        {success && (
          <Alert severity="success" className="mb-16">
            {t("table.userCreatedSuccess")}
          </Alert>
        )}

        {error && (
          <Alert severity="error" className="mb-16">
            {error}
          </Alert>
        )}

        {!loading && !success && (
          <>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              style={{ flexShrink: 0 }}
            >
              <Tab label={t("table.basicInformation")} />
              <Tab label={t("table.selectRules")} />
              <Tab label={t("table.cells")} />
            </Tabs>

            <div className={classes.scrollableBody}>
            {/* Aba 1: Informações Básicas */}
            {activeTab === 0 && (
              <div className={classes.tabPanel}>
                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label={t("table.name")}
                    value={formData.name}
                    type="text"
                    onChange={handleInputChange("name")}
                    required
                    className={`${classes.formField}`}
                    variant="outlined"
                  />

                  <TextField
                    fullWidth
                    label={t("table.email")}
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange("email")}
                    required
                    className={`${classes.formField}`}
                    variant="outlined"
                  />

                  {/* Campos de Company e Tenant - apenas para admins */}
                  {hasRole("ADMIN", "admin") && (
                    <>
                      <Autocomplete
                        fullWidth
                        options={companyOptions}
                        getOptionLabel={(option) => option.name || ""}
                        value={selectedCompany}
                        onChange={(event, newValue) => {
                          selectCompany(newValue);
                        }}
                        onInputChange={(event, newInputValue) => {
                          // Se o campo foi limpo, recarregar lista completa
                          if (newInputValue === "") {
                            searchCompanies();
                          } else if (
                            newInputValue &&
                            newInputValue !== selectedCompany?.name
                          ) {
                            searchCompanies(newInputValue);
                          }
                        }}
                        onOpen={() => {
                          // Carregar lista completa quando abrir o dropdown
                          if (companyOptions.length === 0) {
                            searchCompanies();
                          }
                        }}
                        loading={loadingCompanies}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("table.company")}
                            variant="outlined"
                            className={classes.formField}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingCompanies ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={(option) => (
                          <Box>
                            <Typography variant="body1">
                              {option.name}
                            </Typography>
                          </Box>
                        )}
                      />

                      <Autocomplete
                        fullWidth
                        options={tenantOptions}
                        getOptionLabel={(option) => option.name || ""}
                        value={selectedTenant}
                        onChange={(event, newValue) => {
                          setSelectedTenant(newValue);
                        }}
                        onInputChange={(event, newInputValue) => {
                          // Se o campo foi limpo, recarregar lista completa
                          if (newInputValue === "" && selectedCompany) {
                            searchTenants();
                          } else if (
                            newInputValue &&
                            newInputValue !== selectedTenant?.name &&
                            selectedCompany
                          ) {
                            searchTenants(newInputValue);
                          }
                        }}
                        onOpen={() => {
                          // Carregar lista completa quando abrir o dropdown
                          if (selectedCompany && tenantOptions.length === 0) {
                            searchTenants();
                          }
                        }}
                        loading={loadingTenants}
                        disabled={!selectedCompany}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("table.tenant")}
                            variant="outlined"
                            className={classes.formField}
                            disabled={!selectedCompany}
                            placeholder={
                              selectedCompany
                                ? t("table.selectTenant")
                                : "Primeiro selecione uma empresa"
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingTenants ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={(option) => (
                          <Box>
                            <Typography variant="body1">
                              {option.name}
                            </Typography>
                          </Box>
                        )}
                      />
                    </>
                  )}
                </form>
              </div>
            )}

            {/* Aba 2: Seleção de Regras (mesmo layout do modal de detalhes: serviços e produtos) */}
            {activeTab === 1 && (
              <div className={classes.tabPanel}>
                <Typography variant="h6" gutterBottom>
                  {t("table.selectRulesDescription")}
                </Typography>

                {rulesValidationError && (
                  <Alert severity="error" className="mb-16">
                    {rulesValidationError}
                  </Alert>
                )}

                <div className={classes.rulesTreeContainer}>
                  <ServiceTree
                    services={services}
                    userServices={userServicesForTree}
                    products={products}
                    userProducts={userProductsForTree}
                    selectedUser={{ id: "new" }}
                    onRuleToggle={handleRuleToggle}
                    onProductRuleToggle={handleProductRuleToggle}
                    onServiceToggle={handleServiceToggle}
                    hasRole={hasRole}
                  />
                </div>

                {(selectedRules.length > 0 || selectedProductRules.length > 0) && (
                  <div className={classes.selectedRulesInfo}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("table.selectedRules")} (
                      {selectedRules.length + selectedProductRules.length}):
                    </Typography>
                    {selectedRules.map((rule, index) => {
                      const service = services.find((s) => String(s.service_id) === String(rule.serviceId));
                      const ruleData = service?.rules?.find((r) => String(r.rule_id) === String(rule.ruleId));
                      const ruleLabel = ruleData?.rule_name || ruleData?.rule_code || (rule.ruleId != null && String(rule.ruleId) !== "NaN" ? `ID ${rule.ruleId}` : "Regra");
                      const serviceLabel = service?.service_description || service?.service_name || "";
                      return (
                        <Chip
                          key={`s-${index}`}
                          label={serviceLabel ? `${serviceLabel} - ${ruleLabel}` : ruleLabel}
                          size="small"
                          style={{ margin: 2 }}
                        />
                      );
                    })}
                    {selectedProductRules.map((rule, index) => {
                      const prod = products.find((p) => p.product_code === rule.productCode) ||
                        services.flatMap((s) => s.products || []).find((p) => p.product_code === rule.productCode);
                      const ruleData = prod?.rules?.find((r) => String(r.rule_id) === String(rule.ruleId));
                      const ruleLabel = ruleData?.rule_name || ruleData?.rule_code || (rule.ruleId != null && String(rule.ruleId) !== "NaN" ? `ID ${rule.ruleId}` : "Regra");
                      const prodLabel = prod?.product_name || rule.productCode;
                      return (
                        <Chip
                          key={`p-${index}`}
                          label={`${prodLabel} - ${ruleLabel}`}
                          size="small"
                          style={{ margin: 2 }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Aba 3: Células */}
            {activeTab === 2 && (
              <div className={classes.tabPanel}>
                <Typography variant="h6" gutterBottom>
                  {t("table.userCellsDescription") || "Selecione as células que o usuário poderá acessar."}
                </Typography>
                {loadingCells ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box border={1} borderColor="divider" borderRadius={1} p={1} mt={1} style={{ minHeight: 120 }}>
                    {cells.length === 0 ? (
                      <Typography color="textSecondary">
                        {tenantIdForCells ? (t("table.noCellsInTenant") || "Nenhuma célula neste tenant.") : "Selecione empresa e tenant na primeira aba."}
                      </Typography>
                    ) : (
                      cells.map((cell) => (
                        <FormControlLabel
                          key={cell.id}
                          control={
                            <Checkbox
                              checked={selectedCells.includes(cell.id)}
                              onChange={(e) => handleCellToggle(cell.id, e.target.checked)}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1">{cell.name}</Typography>
                              {cell.description && (
                                <Typography variant="body2" color="textSecondary">{cell.description}</Typography>
                              )}
                            </Box>
                          }
                        />
                      ))
                    )}
                  </Box>
                )}
                {selectedCells.length > 0 && (
                  <div className={classes.selectedRulesInfo} style={{ marginTop: 16 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("table.selectedCells")} ({selectedCells.length}):
                    </Typography>
                    {selectedCells.map((cellId) => {
                      const cell = cells.find((c) => c.id === cellId);
                      return (
                        <Chip
                          key={cellId}
                          label={cell?.name || `Célula ${cellId}`}
                          size="small"
                          style={{ margin: 2 }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary" disabled={loading}>
          {t("actions.cancel")}
        </Button>

        {activeTab === 0 && (
          <Button
            onClick={() => setActiveTab(1)}
            color="primary"
            variant="outlined"
            disabled={loading || success}
          >
            {t("actions.next")}
          </Button>
        )}

        {activeTab === 1 && (
          <>
            <Button
              onClick={() => setActiveTab(0)}
              color="primary"
              variant="outlined"
              disabled={loading || success}
            >
              {t("actions.previous")}
            </Button>
            <Button
              onClick={() => setActiveTab(2)}
              color="primary"
              variant="outlined"
              disabled={loading || success || (selectedRules.length === 0 && selectedProductRules.length === 0)}
            >
              {t("actions.next")}
            </Button>
          </>
        )}

        {activeTab === 2 && (
          <>
            <Button
              onClick={() => setActiveTab(1)}
              color="primary"
              variant="outlined"
              disabled={loading || success}
            >
              {t("actions.previous")}
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={
                loading ||
                success ||
                (selectedRules.length === 0 && selectedProductRules.length === 0) ||
                !formData.name ||
                !formData.email
              }
            >
              {loading ? t("actions.creating") : t("actions.createUser")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateUserModal;
